import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _uniqBy from 'lodash/uniqBy';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { ColoredBackground } from 'reaxl';

import queryDuck from '@/ducks/queryDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSpotlightDuck from '@/ducks/srp/srpSpotlightDuck.js';
import srpSupplementalDuck from '@/ducks/srp/srpSupplementalDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import alphaModule from '@/modules/AlphaModule';

import AtcBranding from '@/components/AtcBranding';
import CompareDrawerPlaceholder from '@/components/CompareDrawerPlaceholder';
import LazyComponent from '@/components/LazyComponent';
import { BackToResultsLinkPlaceholder } from '@/components/vehicledetails/BackToResultsLink';
import VdpPaginationButtons from '@/components/vehicledetails/VdpPaginationButtons';

const CompareListingsDrawer = dynamic(() => import(
    /* webpackChunkName: "CompareListingsDrawer" */
    '@/components/CompareListingsDrawer'
), {
    loading: () => (<CompareDrawerPlaceholder />),
    ssr: false,
});

const CompareListingsDrawerMobile = dynamic(() => import(
    /* webpackChunkName: "CompareListingsDrawer" */
    '@/components/CompareListingsDrawer'
), {
    loading: () => (<CompareDrawerPlaceholder isCollapsed />),
    ssr: false,
});

const BackToResultsLink = dynamic(
    /* webpackChunkName: "BackToResultsLink" */
    () => import('@/components/vehicledetails/BackToResultsLink'),
    {
        loading: () => <BackToResultsLinkPlaceholder />,
        ssr: false,
    }
);

/**
 * wrapStyle support super small device, so that the user has to side scroll
 */
const wrapStyle = {
    overflowX: 'auto',
};

/**
 * VDP pagination container component that renders navigation controls and compare functionality
 * for vehicle detail pages. Manages the display of back-to-results links, pagination buttons,
 * and compare drawers based on feature flags and device type.
 *
 * @component
 * @returns {JSX.Element} The VDP pagination container with navigation controls
 */
export function VdpPaginationContainer() {
    const {
        brand: [, {
            atc_branding: showAtcBranding,
            channel,
        }],
        compare: [enableCompare],
    } = useFeatures([
        'brand',
        'compare',
    ]);

    const device = useDevice();
    const isXs = device.mediaType === 'xs';

    const { brands, isBrand } = useBrand();

    const { id: listingId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);

    const query = useSelector(queryDuck.selectors.getDuckState);
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);

    const inventoryListings = useSelector(srpResultsDuck.selectors.getActiveInventory);
    const { listings: alphaListingIds } = useSelector(alphaModule.duck.selectors.getDuckState);
    const spotlightListingIds = useSelector(srpSpotlightDuck.selectors.getActiveResults);
    const supplementalListingIds = useSelector(srpSupplementalDuck.selectors.getActiveResults);

    let alphaListing;
    if (alphaListingIds && alphaListingIds.length > 0) {
        alphaListing = { listingId: alphaListingIds[0], clickType: 'alpha' };
    }
    const paginationListingIds = [
        alphaListing,
        ...spotlightListingIds.map((id) => ({ listingId: id, clickType: 'spotlight' })),
        ...inventoryListings.map(({ id }) => ({ listingId: id, clickType: 'listing' })),
        ...supplementalListingIds.map((id) => ({ listingId: id, clickType: 'supplemental' })),
    ].filter(Boolean);

    const listingIds = _uniqBy(paginationListingIds, 'listingId');

    const [showCompareDrawer, setShowCompareDrawer] = useState(false);

    useEffect(() => {
        if (enableCompare) {
            setShowCompareDrawer(compareToggle);
        }
    }, [enableCompare, compareToggle]);

    const renderBackToResultsLink = () => (
        <LazyComponent
            key="back-to-results-link"
            fallback={<BackToResultsLinkPlaceholder />}
            height={64}
        >
            <BackToResultsLink />
        </LazyComponent>
    );

    const shouldRenderPaginationButtons = (
        query?.clickType !== 'similar'
        && resultCount > 0
        && listingIds
        && listingIds.length > 0
    );

    const renderPaginationButtons = () => (
        <VdpPaginationButtons
            channel={channel}
            listingId={listingId}
            listingIds={listingIds || []}
            query={query}
        />
    );

    const renderMobileLayout = () => {
        if (showAtcBranding) {
            return (
                <>
                    {shouldRenderPaginationButtons && (
                        <AtcBranding className="text-right margin-top-2" />
                    )}
                    <div className="display-flex align-items-center justify-content-between">
                        {renderBackToResultsLink()}
                        {shouldRenderPaginationButtons ? renderPaginationButtons() : (<AtcBranding />)}
                    </div>
                </>
            );
        }

        return (
            <div className="display-flex align-items-center justify-content-between">
                {renderBackToResultsLink()}
                {shouldRenderPaginationButtons && renderPaginationButtons()}
            </div>
        );
    };

    const renderDesktopLayout = () => {
        if (showAtcBranding) {
            return (
                <div className="display-flex align-items-center">
                    <div className="display-flex align-items-center">
                        {renderBackToResultsLink()}
                        {shouldRenderPaginationButtons && renderPaginationButtons()}
                    </div>
                    <AtcBranding className="margin-left-auto" />
                </div>
            );
        }

        return (
            <div className="display-flex align-items-center justify-content-between">
                {renderBackToResultsLink()}
                {shouldRenderPaginationButtons && renderPaginationButtons()}
            </div>
        );
    };

    return (
        <>
            <ColoredBackground
                className="container-fluid"
                style={wrapStyle}
            >
                <div
                    className={clsx('container', {
                        'margin-top-4': isBrand(brands.KBB_BRAND),
                        'padding-left-0 padding-right-0': isXs,
                    })}
                >
                    {isXs ? renderMobileLayout() : renderDesktopLayout()}
                </div>
            </ColoredBackground>
            {showCompareDrawer && isXs && (
                <LazyComponent
                    key="compare-drawer-mobile"
                    fallback={<CompareDrawerPlaceholder isCollapsed />}
                >
                    <CompareListingsDrawerMobile
                        isCollapsible
                        parent="vdp-paginate"
                    />
                </LazyComponent>
            )}
            {showCompareDrawer && !isXs && (
                <LazyComponent
                    key="compare-drawer-desktop"
                    fallback={<CompareDrawerPlaceholder />}
                >
                    <CompareListingsDrawer
                        isCollapsible
                        parent="vdp-paginate"
                    />
                </LazyComponent>
            )}
        </>
    );
}

export default VdpPaginationContainer;
